import { useState, useEffect, useMemo, useRef } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Button,
  Text,
  Spinner,
  Field,
  Input,
  Select,
  Combobox,
  Option,
  Checkbox,
  RadioGroup,
  Radio,
  Textarea,
  makeStyles,
  mergeClasses,
  tokens,
  MessageBar,
  MessageBarBody,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
} from '@fluentui/react-components'
import { ArrowLeft24Regular, Save24Regular, PersonSearch24Regular, Delete24Regular, DocumentCopy24Regular, Add24Regular, Edit24Regular } from '@fluentui/react-icons'
import { ChevronUp20Regular, ChevronDown20Regular } from '@fluentui/react-icons'
import { employeesService, CreateEmployeeRequest, WorkSchedule } from '../../services/employeesService'
import { contractorsService } from '../../services/contractorsService'
import { validateCzPersonalId, formatCzPersonalId } from '../../utils/czPersonalId'
import { referenceDataService } from '../../services/referenceDataService'
import { configService, CONFIG_KEYS } from '../../services/configService'
import { searchEntraUsers, getEntraUserManager, EntraUser } from '../../services/graphService'
import { NativeDateInput } from '../../components/NativeDateInput'
import { CzPhoneInput } from '../../components/CzPhoneInput'
import { useAuth } from '../../auth/useAuth'
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges'
import { UnsavedChangesDialog } from '../../components/UnsavedChangesDialog'

// Vyhledávání manažera z existujících zaměstnanců
async function searchEmployees(query: string) {
  if (!query || query.length < 2) return []
  const { employeesService } = await import('../../services/employeesService')
  const result = await employeesService.getAll({ search: query, pageSize: 10, excludeTerminated: true })
  return result.items
}

const useStyles = makeStyles({
  pageHeader: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' },
  card: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: '12px',
    boxShadow: tokens.shadow4,
    marginBottom: '16px',
  },
  changedStripe: { height: '3px', backgroundColor: tokens.colorBrandBackground, borderRadius: '12px 12px 0 0' },
  cardHeader: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '14px 24px', cursor: 'pointer',
    ':hover': { backgroundColor: tokens.colorNeutralBackground2 },
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  cardTitle: { fontSize: tokens.fontSizeBase400, fontWeight: tokens.fontWeightSemibold },
  fieldRow: {
    display: 'grid', gridTemplateColumns: '1fr 1fr',
    borderBottom: `1px solid ${tokens.colorNeutralBackground3}`,
  },
  fieldRowFull: { gridTemplateColumns: '1fr' },
  fieldCell: { padding: '10px 24px 12px', display: 'flex', flexDirection: 'column', gap: '6px' },
  fieldCellBorderRight: { borderRight: `1px solid ${tokens.colorNeutralBackground3}` },
  fieldLabel: {
    fontSize: '11px', fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground3,
    textTransform: 'uppercase', letterSpacing: '0.5px',
    display: 'flex', alignItems: 'center', gap: '5px',
  },
  fieldLabelChanged: { color: tokens.colorBrandForeground1 },
  changedDot: {
    width: '6px', height: '6px', borderRadius: '50%',
    backgroundColor: tokens.colorBrandBackground, flexShrink: 0,
  },
  hint: { fontSize: '11px', color: tokens.colorNeutralForeground3 },
  searchRow: { display: 'flex', gap: '6px', alignItems: 'center' },
  resultsList: {
    position: 'absolute', zIndex: 100, width: '100%', top: '100%', left: 0,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusMedium,
    marginTop: '4px', maxHeight: '200px', overflowY: 'auto',
    backgroundColor: tokens.colorNeutralBackground1, boxShadow: tokens.shadow8,
  },
  resultItem: {
    padding: '8px 12px', cursor: 'pointer',
    ':hover': { backgroundColor: tokens.colorNeutralBackground3 },
  },
  dayGrid: { display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: '8px' },
  floatingBar: {
    position: 'sticky', bottom: '16px',
    display: 'flex', alignItems: 'center', gap: '10px',
    backgroundColor: '#1f1f1f', borderRadius: '12px',
    padding: '12px 20px',
    boxShadow: '0 8px 32px rgba(0,0,0,.25)',
    marginTop: '8px', zIndex: 10,
  },
  floatingHint: { fontSize: '13px', color: 'rgba(255,255,255,.6)', flex: '1' },
})

const CONTRACT_TYPES = [
  { value: 1, label: 'Na dobu neurčitou' },
  { value: 2, label: 'Na dobu určitou' },
]

const EMPLOYMENT_TYPES = [
  { value: 1, label: 'HPP – Hlavní pracovní poměr' },
  { value: 2, label: 'DPP – Dohoda o provedení práce' },
  { value: 3, label: 'DPC – Dohoda o pracovní činnosti' },
]

const DAY_KEYS: (keyof WorkSchedule)[] = ['mon', 'tue', 'wed', 'thu', 'fri']
const DAY_LABELS: Record<keyof WorkSchedule, string> = {
  mon: 'Pondělí', tue: 'Úterý', wed: 'Středa', thu: 'Čtvrtek', fri: 'Pátek',
}

const DEFAULT_SCHEDULE: WorkSchedule = { mon: 8, tue: 8, wed: 8, thu: 8, fri: 8 }

interface FormState extends Omit<CreateEmployeeRequest, 'departmentName' | 'positionName'> {
  workingRatioPercent: number
  weeklyHours: number
  workloadValidFrom: string
  workloadIsNew: boolean            // #23 Fáze 2: true = založit nové období od data; false = upravit stávající aktuální úvazek
  scheduleMode: 'standard' | 'custom'
  status: number
  statusFrom: string
  managerName: string
  departmentName: string
  positionName: string
  contractType: number
  contractEndDate: string
  probationEndDate: string
  personalId: string
  healthInsurance: string
  healthInsuranceCode: string
  bankAccount: string
  vehicle: string
  nextOfKinName: string
  nextOfKinAddress: string
  nextOfKinEmail: string
  nextOfKinPhone: string
  country: string
  child1Name: string
  child1PersonalId: string
  child1Studies: boolean
  child2Name: string
  child2PersonalId: string
  child2Studies: boolean
  child3Name: string
  child3PersonalId: string
  child3Studies: boolean
  child4Name: string
  child4PersonalId: string
  child4Studies: boolean
  child5Name: string
  child5PersonalId: string
  child5Studies: boolean
  note: string
}

const emptyForm: FormState = {
  firstName:           '',
  lastName:            '',
  email:               '',
  phone:               '',
  costCenter:          '',
  personalEmail:       '',
  personalPhone:       '',
  nextOfKinName:       '',
  nextOfKinAddress:    '',
  nextOfKinEmail:      '',
  nextOfKinPhone:      '',
  address:             '',
  city:                '',
  postalCode:          '',
  country:             'CZ',
  departmentName:      '',
  positionName:        '',
  birthDate:           '',
  personalNumber:      '',
  hasMealVouchers:     true,
  hireDate:            new Date().toISOString().split('T')[0],
  cooperationStartDate: '',
  employmentType:      1,
  workingRatioPercent: 100,
  weeklyHours:         40,
  workloadValidFrom:   '',
  workloadIsNew:       true,
  scheduleMode:        'standard',
  status:              1,
  statusFrom:          '',
  managerName:         '',
  contractType:        1,
  contractEndDate:     '',
  probationEndDate:    '',
  personalId:          '',
  healthInsurance:     '',
  healthInsuranceCode: '',
  bankAccount:         '',
  vehicle:             '',
  child1Name:          '',
  child1PersonalId:    '',
  child1Studies:       false,
  child2Name:          '',
  child2PersonalId:    '',
  child2Studies:       false,
  child3Name:          '',
  child3PersonalId:    '',
  child3Studies:       false,
  child4Name:          '',
  child4PersonalId:    '',
  child4Studies:       false,
  child5Name:          '',
  child5PersonalId:    '',
  child5Studies:       false,
  note:                '',
}

// #23 Fáze 2: editor historie úvazku v režimu úpravy zaměstnance.
// Aktuální (nejnovější) úvazek se nastavuje výše hlavními poli; zde se upravují/odebírají
// starší období. Zobrazí se jen pokud existuje více než jedno období.
function WorkloadHistoryEditor({ employeeId }: { employeeId: number }) {
  const styles = useStyles()
  const queryClient = useQueryClient()
  const { data = [] } = useQuery({
    queryKey: ['workload-history', employeeId],
    queryFn: () => employeesService.getWorkloadHistory(employeeId),
    enabled: employeeId > 0,
  })

  const [editingId, setEditingId] = useState<number | null>(null)
  const [editFrom, setEditFrom] = useState('')
  const [editHours, setEditHours] = useState('')
  const [deleteId, setDeleteId] = useState<number | null>(null)
  const [err, setErr] = useState('')

  const parseDecimal = (s: string) => Number((s ?? '').replace(',', '.'))
  const fmtH = (h: number) => (h % 1 === 0 ? String(h) : h.toFixed(1).replace('.', ','))
  const weeklyOf = (json: string | null, fallback: number) => {
    if (!json) return fallback
    try { const s = JSON.parse(json); return ['mon', 'tue', 'wed', 'thu', 'fri'].reduce((a, d) => a + (s[d] ?? 0), 0) } catch { return fallback }
  }
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['workload-history', employeeId] })
    queryClient.invalidateQueries({ queryKey: ['employee'] })
    queryClient.invalidateQueries({ queryKey: ['vacation-balance', employeeId] })
  }
  const updateMutation = useMutation({
    mutationFn: (v: { periodId: number; data: { validFrom: string; weeklyHours: number } }) =>
      employeesService.updateWorkloadPeriod(employeeId, v.periodId, v.data),
    onSuccess: () => { invalidate(); setEditingId(null); setErr('') },
    onError: () => setErr('Období s tímto datem už existuje nebo se nepodařilo uložit.'),
  })
  const deleteMutation = useMutation({
    mutationFn: (periodId: number) => employeesService.deleteWorkloadPeriod(employeeId, periodId),
    onSuccess: () => { invalidate(); setDeleteId(null); setErr('') },
    onError: () => { setErr('Období se nepodařilo smazat.'); setDeleteId(null) },
  })

  if (data.length <= 1) return null   // jen jedno období → není co z historie upravovat

  const startEdit = (p: typeof data[number]) => {
    setErr('')
    setEditingId(p.id)
    setEditFrom(p.validFrom.split('T')[0])
    setEditHours(fmtH(weeklyOf(p.workScheduleJson, p.weeklyHours)))
  }
  const saveEdit = () => {
    const h = parseDecimal(editHours)
    if (!editFrom) { setErr('Zadejte datum platnosti.'); return }
    if (isNaN(h) || h <= 0) { setErr('Zadejte platný počet hodin.'); return }
    updateMutation.mutate({ periodId: editingId!, data: { validFrom: editFrom, weeklyHours: h } })
  }

  return (
    <div className={mergeClasses(styles.fieldRow, styles.fieldRowFull)}>
      <div className={styles.fieldCell}>
        {cellLabelStatic('Historie úvazku')}
        <span className={styles.hint} style={{ marginBottom: 8 }}>
          Aktuální úvazek nastavíte výše („Nový úvazek platí od"). Zde upravíte nebo odeberete starší období.
        </span>

        {err && <MessageBar intent="error" style={{ marginBottom: 8 }}><MessageBarBody>{err}</MessageBarBody></MessageBar>}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, border: `1px solid ${tokens.colorNeutralStroke2}`, borderRadius: 6, padding: '8px 12px' }}>
          {data.map((p, i) => {
            const weekly = weeklyOf(p.workScheduleJson, p.weeklyHours)
            const isCurrent = i === 0
            if (editingId === p.id) {
              return (
                <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: '2px 0' }}>
                  <span style={{ fontSize: 12, color: tokens.colorNeutralForeground3 }}>Platí od</span>
                  <NativeDateInput value={editFrom} onChange={setEditFrom} />
                  <Input type="text" inputMode="decimal" placeholder="h/týden" style={{ width: 100 }}
                    value={editHours} onChange={e => setEditHours((e.target?.value ?? '').replace(/[^0-9.,]/g, ''))} />
                  <Button size="small" appearance="primary" disabled={updateMutation.isPending} onClick={saveEdit}>Uložit</Button>
                  <Button size="small" appearance="subtle" onClick={() => { setEditingId(null); setErr('') }}>Zrušit</Button>
                  {p.workScheduleJson && (
                    <span style={{ fontSize: 11, color: tokens.colorNeutralForeground3, width: '100%' }}>
                      Období má rozpis hodin po dnech — úprava jej nahradí rovnoměrným týdenním úvazkem.
                    </span>
                  )}
                </div>
              )
            }
            return (
              <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '2px 0' }}>
                <span style={{ minWidth: 92, fontWeight: 600 }}>od {new Date(p.validFrom).toLocaleDateString('cs-CZ')}</span>
                <span>{fmtH(weekly)} h/týden <span style={{ color: tokens.colorNeutralForeground3 }}>({Math.round(weekly / 40 * 100)} %)</span></span>
                {isCurrent && <span style={{ fontSize: 12, color: tokens.colorBrandForeground1 }}>· aktuální</span>}
                {!isCurrent && editingId === null && (
                  <span style={{ marginLeft: 'auto', display: 'flex', gap: 2 }}>
                    <Button size="small" appearance="subtle" icon={<Edit24Regular />} aria-label="Upravit období" onClick={() => startEdit(p)} />
                    <Button size="small" appearance="subtle" aria-label="Smazat období"
                      style={{ color: tokens.colorPaletteRedForeground1 }} onClick={() => { setErr(''); setDeleteId(p.id) }}>×</Button>
                  </span>
                )}
              </div>
            )
          })}
        </div>
      </div>

      <Dialog open={deleteId !== null} onOpenChange={(_, d) => { if (!d.open) setDeleteId(null) }}>
        <DialogSurface style={{ minWidth: 420 }}>
          <DialogTitle>Smazat období úvazku</DialogTitle>
          <DialogBody>
            <DialogContent>Opravdu smazat toto období z historie úvazku? Tato akce je nevratná.</DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setDeleteId(null)}>Zrušit</Button>
              <Button appearance="primary" disabled={deleteMutation.isPending}
                onClick={() => deleteMutation.mutate(deleteId!)}>
                {deleteMutation.isPending ? 'Mažu...' : 'Smazat'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  )
}

// Popisek pole ve stejném stylu jako cellLabel (bez indikace změny) — pro samostatné komponenty.
function cellLabelStatic(label: string) {
  return <span style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', color: tokens.colorNeutralForeground3, letterSpacing: '0.03em' }}>{label}</span>
}

export default function EmployeeFormPage() {
  const styles = useStyles()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { id } = useParams<{ id: string }>()
  const isEdit = !!id
  const queryClient = useQueryClient()

  const { isHR, isAdmin } = useAuth()
  const canDelete = isHR || isAdmin

  // Kopie zaměstnance — předvyplnění z detailu
  const copySource = (location.state as any)?.copyFrom
  const copyInitialForm: FormState | null = copySource ? {
    ...emptyForm,
    firstName:           copySource.firstName  ?? '',
    lastName:            copySource.lastName   ?? '',
    email:               copySource.email      ?? '',
    phone:               copySource.phone      ?? '',
    costCenter:          copySource.costCenter ?? '',
    personalEmail:       copySource.personalEmail ?? '',
    personalPhone:       copySource.personalPhone ?? '',
    nextOfKinName:       copySource.nextOfKinName ?? '',
    nextOfKinAddress:    copySource.nextOfKinAddress ?? '',
    nextOfKinEmail:      copySource.nextOfKinEmail ?? '',
    nextOfKinPhone:      copySource.nextOfKinPhone ?? '',
    address:             copySource.address    ?? '',
    city:                copySource.city       ?? '',
    postalCode:          copySource.postalCode ?? '',
    country:             copySource.country    ?? 'CZ',
    departmentName:      copySource.departmentName ?? '',
    positionName:        copySource.positionName  ?? '',
    birthDate:           copySource.birthDate  ?? '',
    personalId:          copySource.personalId ?? '',
    hasMealVouchers:     copySource.hasMealVouchers ?? true,
    cooperationStartDate: copySource.cooperationStartDate?.split('T')[0] ?? '',
    employmentType:      copySource.employmentType ?? 1,
    workingRatioPercent: copySource.workingRatioPercent ?? 100,
    weeklyHours:         copySource.weeklyHours ?? 40,
    workloadValidFrom:   '',
    workloadIsNew:       true,
    scheduleMode:        'standard',
    managerId:           copySource.managerId,
    managerName:         copySource.managerFullName ?? '',
    healthInsurance:     copySource.healthInsurance ?? '',
    healthInsuranceCode: copySource.healthInsuranceCode ?? '',
    bankAccount:         copySource.bankAccount ?? '',
    vehicle:             copySource.vehicle ?? '',
    child1Name:          copySource.child1Name ?? '',
    child1PersonalId:    copySource.child1PersonalId ?? '',
    child1Studies:       copySource.child1Studies ?? false,
    child2Name:          copySource.child2Name ?? '',
    child2PersonalId:    copySource.child2PersonalId ?? '',
    child2Studies:       copySource.child2Studies ?? false,
    child3Name:          copySource.child3Name ?? '',
    child3PersonalId:    copySource.child3PersonalId ?? '',
    child3Studies:       copySource.child3Studies ?? false,
    child4Name:          copySource.child4Name ?? '',
    child4PersonalId:    copySource.child4PersonalId ?? '',
    child4Studies:       copySource.child4Studies ?? false,
    child5Name:          copySource.child5Name ?? '',
    child5PersonalId:    copySource.child5PersonalId ?? '',
    child5Studies:       copySource.child5Studies ?? false,
    entraObjectId:       copySource.entraObjectId ?? '',
    note:                copySource.note ?? '',
    // Resetováno
    hireDate:            '',
    contractType:        1,
    contractEndDate:     '',
    probationEndDate:    '',
    statusFrom:          '',
    status:              2,   // Nenastoupil
    personalNumber:      '',
  } : null

  const parseDecimal = (s: string) => Number(s.replace(',', '.'))

  const [form, setForm] = useState<FormState>(copyInitialForm ?? emptyForm)
  const [schedule, setSchedule] = useState<WorkSchedule>(DEFAULT_SCHEDULE)
  const [dayInputs, setDayInputs] = useState<Partial<Record<keyof WorkSchedule, string>>>({})
  const [error, setError] = useState<string | null>(null)

  // ── Dynamické řádky dětí ──────────────────────────────────────────────────
  type ChildRow = { name: string; personalId: string; studies: boolean }

  const [childRows, setChildRows] = useState<ChildRow[]>(() =>
    copyInitialForm
      ? [1,2,3,4,5]
          .map(n => ({
            name:       (copyInitialForm[`child${n}Name`       as keyof FormState] as string) ?? '',
            personalId: (copyInitialForm[`child${n}PersonalId` as keyof FormState] as string) ?? '',
            studies:    !!(copyInitialForm[`child${n}Studies`   as keyof FormState]),
          }))
          .filter(r => r.name.trim() !== '' || r.personalId.trim() !== '')
      : []
  )

  const updateChildRows = (rows: ChildRow[]) => {
    const pad = Array.from({ length: 5 }, (_, i) =>
      rows[i] ?? { name: '', personalId: '', studies: false })
    setChildRows(rows)
    setForm(f => ({
      ...f,
      child1Name: pad[0].name, child1PersonalId: pad[0].personalId, child1Studies: pad[0].studies,
      child2Name: pad[1].name, child2PersonalId: pad[1].personalId, child2Studies: pad[1].studies,
      child3Name: pad[2].name, child3PersonalId: pad[2].personalId, child3Studies: pad[2].studies,
      child4Name: pad[3].name, child4PersonalId: pad[3].personalId, child4Studies: pad[3].studies,
      child5Name: pad[4].name, child5PersonalId: pad[4].personalId, child5Studies: pad[4].studies,
    }))
  }

  // Stravenky: dostupné jen pro HPP (1) + Aktivní (1)
  const mealVouchersEligible = form.employmentType === 1 && form.status === 1
  useEffect(() => {
    if (!mealVouchersEligible) {
      setForm(f => ({ ...f, hasMealVouchers: false }))
    }
  }, [mealVouchersEligible])

  // Detekce neuložených změn
  const savedFormRef = useRef<FormState>(copyInitialForm ?? emptyForm)
  const isDirtyRef = useRef(false)
  isDirtyRef.current = JSON.stringify(form) !== JSON.stringify(savedFormRef.current)
  const blocker = useUnsavedChanges(isDirtyRef)

  const isDirty = JSON.stringify(form) !== JSON.stringify(savedFormRef.current)

  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    basic: true, assignment: true, contract: true,
    schedule: true, private: true, payroll: true, children: true, note: true,
  })
  const toggleSection = (key: string) => setOpenSections(s => ({ ...s, [key]: !s[key] }))

  const isChanged = (field: keyof FormState) =>
    JSON.stringify(form[field]) !== JSON.stringify(savedFormRef.current[field])

  const sectionChanged = (...fields: (keyof FormState)[]) => fields.some(f => isChanged(f))

  const cellLabel = (label: string, field?: keyof FormState | (keyof FormState)[]) => {
    const changed = field !== undefined
      ? (Array.isArray(field) ? (field as (keyof FormState)[]).some(f => isChanged(f)) : isChanged(field as keyof FormState))
      : false
    return (
      <span className={mergeClasses(styles.fieldLabel, changed ? styles.fieldLabelChanged : undefined)}>
        {changed && <span className={styles.changedDot} />}
        {label}
      </span>
    )
  }

  const [personalIdError, setPersonalIdError] = useState<string | null>(null)
  const [deleteOpen, setDeleteOpen] = useState(false)

  const permanentDeleteMutation = useMutation({
    mutationFn: () => employeesService.delete(Number(id)),
    onSuccess: () => {
      isDirtyRef.current = false
      queryClient.invalidateQueries({ queryKey: ['employees'] })
      navigate('/employees')
    },
    onError: (err: any) => {
      const msg = err?.response?.data && typeof err.response.data === 'string'
        ? err.response.data
        : 'Zaměstnance se nepodařilo trvale smazat.'
      setError(msg)
      setDeleteOpen(false)
    },
  })

  // Entra ID vyhledávání
  const IS_MOCK = import.meta.env.VITE_MOCK_AUTH === 'true'
  const [searchResults, setSearchResults] = useState<EntraUser[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [emailContractorInfo, setEmailContractorInfo] = useState<string | null>(null)

  // Reference data — jen jména pro autocomplete
  const { data: departments = [] } = useQuery({
    queryKey: ['departments'],
    queryFn: referenceDataService.getDepartments,
  })

  const { data: positions = [] } = useQuery({
    queryKey: ['positions'],
    queryFn: referenceDataService.getPositions,
  })

  // #23: historie úvazku — pro předvyplnění data platnosti při opravě stávajícího (aktuálního) období
  const { data: workloadHistoryForm = [] } = useQuery({
    queryKey: ['workload-history', Number(id)],
    queryFn: () => employeesService.getWorkloadHistory(Number(id)),
    enabled: isEdit,
    staleTime: 0,
    refetchOnMount: 'always',
  })
  const currentPeriodFrom = workloadHistoryForm[0]?.validFrom?.split('T')[0] ?? ''
  // aktuální období má editovatelný počátek jen pokud NENÍ zároveň nejstarší (víc období);
  // u jediného období se počátek rovná datu nástupu (mění se přes „Datum nástupu")
  const canEditCurrentFrom = workloadHistoryForm.length > 1

  const { data: configEntries = [] } = useQuery({
    queryKey: ['config'],
    queryFn: configService.getAll,
    staleTime: 5 * 60_000,
  })

  const healthInsuranceMap = useMemo((): Record<string, string> => {
    const raw = configEntries.find((c: { key: string }) => c.key === CONFIG_KEYS.HealthInsurances)?.value
    try { return JSON.parse(raw || '{}') } catch { return {} }
  }, [configEntries])

  const healthInsuranceOptions = useMemo(() =>
    Object.entries(healthInsuranceMap).map(([code, name]) => ({ code, name: name as string })),
    [healthInsuranceMap]
  )

  // Návrhy pro Combobox — filtruj pozice dle vybraného oddělení (pokud existuje)
  const deptNames = useMemo(() =>
    [...new Set(departments.map(d => d.name))].sort(), [departments])

  const positionNames = useMemo(() => {
    const matchingDept = form.departmentName
      ? departments.find(d => d.name.toLowerCase() === form.departmentName.toLowerCase())
      : undefined
    const filtered = matchingDept
      ? positions.filter(p => p.departmentId === matchingDept.id)
      : positions
    return [...new Set(filtered.map(p => p.name))].sort()
  }, [positions, departments, form.departmentName])

  const handleEntraSearch = async () => {
    const query = form.email.trim()
    if (query.length < 2) return
    setIsSearching(true)
    try {
      const results = await searchEntraUsers(query)
      setSearchResults(results)
    } catch {
      setSearchResults([])
    } finally {
      setIsSearching(false)
    }
  }

  // Informativní kontrola: patří e-mail aktivnímu externistovi? (souběh rolí je povolen)
  const checkContractorEmail = async (email: string) => {
    const e = email.trim()
    if (!e) { setEmailContractorInfo(null); return }
    try {
      const res = await contractorsService.checkEmail(e)
      setEmailContractorInfo(res.isTaken
        ? `Na tento e-mail už existuje externista ${res.contractorName}. Uložení a souběh rolí je možné.`
        : null)
    } catch {
      // kontrola selhala — bez informace
    }
  }

  const handleSelectEntraUser = async (user: EntraUser) => {
    const selectedEmail = user.mail ?? user.userPrincipalName
    setForm(f => ({
      ...f,
      firstName:     user.givenName || user.displayName.split(' ')[0] || '',
      lastName:      user.surname   || user.displayName.split(' ').slice(1).join(' ') || '',
      email:         selectedEmail,
      entraObjectId: user.id,
    }))
    setSearchResults([])
    setManagerHint(null)
    checkContractorEmail(selectedEmail)

    // Automaticky načti manažera z Entra ID
    try {
      const manager = await getEntraUserManager(user.id)
      if (!manager) return

      const managerEmail = manager.mail ?? manager.userPrincipalName
      const results = await searchEmployees(manager.displayName)
      const found = results.find(e =>
        e.fullName.toLowerCase() === manager.displayName.toLowerCase()
      )
      if (found) {
        setForm(f => ({ ...f, managerId: found.id, managerName: found.fullName }))
      } else {
        setManagerHint(`Manažer „${manager.displayName}" (${managerEmail}) není dosud v systému. Přidejte ho jako zaměstnance přednostně.`)
      }
    } catch {
      // Graf. API nedostupné — přeskočit
    }
  }

  // Vyhledávání manažera
  const [managerQuery, setManagerQuery] = useState('')
  const [managerResults, setManagerResults] = useState<{ id: number; fullName: string; departmentName: string | null }[]>([])
  const [isSearchingManager, setIsSearchingManager] = useState(false)
  const [managerHint, setManagerHint] = useState<string | null>(null)

  const handleManagerSearch = async () => {
    if (managerQuery.length < 2) return
    setIsSearchingManager(true)
    try {
      const results = await searchEmployees(managerQuery)
      setManagerResults(results)
    } catch {
      setManagerResults([])
    } finally {
      setIsSearchingManager(false)
    }
  }

  const handleSelectManager = (emp: { id: number; fullName: string; departmentName: string | null }) => {
    setForm(f => ({ ...f, managerId: emp.id, managerName: emp.fullName }))
    setManagerResults([])
    setManagerQuery('')
  }

  const { data: detail, isLoading: isLoadingDetail } = useQuery({
    queryKey: ['employee', id],
    queryFn: () => employeesService.getById(Number(id)),
    enabled: isEdit,
  })

  // Naplnění formuláře po načtení dat zaměstnance
  useEffect(() => {
    if (detail) {
      const hasCustomSchedule = !!detail.workScheduleJson
      let parsed = DEFAULT_SCHEDULE
      if (detail.workScheduleJson) {
        try { parsed = JSON.parse(detail.workScheduleJson) } catch { /* ignore */ }
      }
      const loaded: FormState = {
        firstName:           detail.firstName,
        lastName:            detail.lastName,
        email:               detail.email,
        phone:               detail.phone ?? '',
        costCenter:          detail.costCenter ?? '',
        personalEmail:       detail.personalEmail ?? '',
        personalPhone:       detail.personalPhone ?? '',
        nextOfKinName:       detail.nextOfKinName ?? '',
        nextOfKinAddress:    detail.nextOfKinAddress ?? '',
        nextOfKinEmail:      detail.nextOfKinEmail ?? '',
        nextOfKinPhone:      detail.nextOfKinPhone ?? '',
        address:             detail.address ?? '',
        city:                detail.city ?? '',
        postalCode:          detail.postalCode ?? '',
        country:             detail.country ?? 'CZ',
        birthDate:           detail.birthDate?.split('T')[0] ?? '',
        personalId:          detail.personalId ?? '',
        healthInsurance:     detail.healthInsurance ?? '',
        healthInsuranceCode: detail.healthInsuranceCode ?? '',
        personalNumber:      detail.personalNumber ?? '',
        hasMealVouchers:     detail.hasMealVouchers ?? true,
        managerName:         detail.managerFullName ?? '',
        hireDate:            detail.hireDate?.split('T')[0] ?? detail.hireDate ?? '',
        cooperationStartDate: detail.cooperationStartDate?.split('T')[0] ?? '',
        employmentType:      detail.employmentType,
        workingRatioPercent: detail.workingRatioPercent ?? 100,
        weeklyHours:         detail.weeklyHours ?? 40,
        workloadValidFrom:   new Date().toISOString().split('T')[0],   // #23 Fáze 2: předvyplněno dneškem (nové období)
        workloadIsNew:       true,                                      // default: změna úvazku = nové období do historie
        scheduleMode:        hasCustomSchedule ? 'custom' : 'standard',
        status:              detail.status ?? 1,
        statusFrom:          detail.statusFrom?.split('T')[0] ?? '',
        contractType:        detail.contractType ?? 1,
        contractEndDate:     detail.contractEndDate?.split('T')[0] ?? '',
        probationEndDate:    detail.probationEndDate?.split('T')[0] ?? '',
        departmentName:      detail.departmentName ?? '',
        positionName:        detail.positionName ?? '',
        managerId:           detail.managerId ?? undefined,
        entraObjectId:       detail.entraObjectId ?? undefined,
        bankAccount:         detail.bankAccount ?? '',
        vehicle:             detail.vehicle ?? '',
        child1Name:          detail.child1Name ?? '',
        child1PersonalId:    detail.child1PersonalId ?? '',
        child1Studies:       detail.child1Studies ?? false,
        child2Name:          detail.child2Name ?? '',
        child2PersonalId:    detail.child2PersonalId ?? '',
        child2Studies:       detail.child2Studies ?? false,
        child3Name:          detail.child3Name ?? '',
        child3PersonalId:    detail.child3PersonalId ?? '',
        child3Studies:       detail.child3Studies ?? false,
        child4Name:          detail.child4Name ?? '',
        child4PersonalId:    detail.child4PersonalId ?? '',
        child4Studies:       detail.child4Studies ?? false,
        child5Name:          detail.child5Name ?? '',
        child5PersonalId:    detail.child5PersonalId ?? '',
        child5Studies:       detail.child5Studies ?? false,
        note:                detail.note ?? '',
      }
      setForm(loaded)
      savedFormRef.current = loaded
      setSchedule(parsed)
      setDayInputs({})
      setChildRows([1,2,3,4,5]
        .map(n => ({
          name:       (detail as any)[`child${n}Name`]       ?? '',
          personalId: (detail as any)[`child${n}PersonalId`] ?? '',
          studies:    (detail as any)[`child${n}Studies`]    ?? false,
        }))
        .filter((r: ChildRow) => r.name.trim() !== '' || r.personalId.trim() !== ''))
    }
  }, [detail])

  // Týdenní hodiny: vlastní rozvrh = součet po dnech; standard = zadané hodiny/týden (#23 Fáze 1)
  const weeklyHours = useMemo(() => {
    if (form.scheduleMode === 'custom') {
      return DAY_KEYS.reduce((s, d) => s + (schedule[d] ?? 0), 0)
    }
    return Number(form.weeklyHours) || 0
  }, [form.scheduleMode, form.weeklyHours, schedule])

  const mutation = useMutation({
    mutationFn: (data: CreateEmployeeRequest) =>
      isEdit ? employeesService.update(Number(id), data) : employeesService.create(data),
    onSuccess: (result: any) => {
      isDirtyRef.current = false
      queryClient.invalidateQueries({ queryKey: ['employees'] })
      queryClient.invalidateQueries({ queryKey: ['employee', id] })
      // #23 Fáze 2: po změně úvazku je potřeba obnovit i historii a dovolenou (jinak detail ukáže cache)
      const numId = Number(isEdit ? id : result?.id)
      if (numId) {
        queryClient.invalidateQueries({ queryKey: ['workload-history', numId] })
        queryClient.invalidateQueries({ queryKey: ['vacation-balance', numId] })
      }
      const newId = isEdit ? id : result?.id
      navigate(newId ? `/employees/${newId}` : '/employees')
    },
    onError: (err: any) => {
      const data = err?.response?.data
      if (Array.isArray(data) && data[0]?.errorMessage)
        setError(data.map((e: any) => e.errorMessage).join(' '))
      else if (typeof data === 'string' && data)
        setError(data)
      else if (data?.title)
        setError(`${data.title}: ${data.detail ?? ''}`)
      else if (err?.response?.status)
        setError(`Chyba HTTP ${err.response.status}: ${JSON.stringify(data ?? '').substring(0, 200)}`)
      else
        setError(`Síťová chyba: ${err?.message ?? 'neznámá'} (kód: ${err?.code ?? '?'})`)
    },
  })

  const set = (field: keyof FormState) => (e: any) => {
    const value = e.target?.value ?? e
    setForm(f => ({ ...f, [field]: value }))
  }

  const setDay = (day: keyof WorkSchedule) => (e: any) => {
    const raw = (e.target?.value ?? '').replace(/[^0-9.,]/g, '')
    setDayInputs(s => ({ ...s, [day]: raw }))
    const val = parseDecimal(raw)
    setSchedule(s => ({ ...s, [day]: isNaN(val) ? 0 : Math.max(0, Math.min(24, val)) }))
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    if (!form.firstName || !form.lastName || !form.hireDate) {
      setError('Vyplňte prosím všechna povinná pole (*).')
      return
    }

    const pidErr = validateCzPersonalId(form.personalId ?? '')
    setPersonalIdError(pidErr)
    if (pidErr) return

    const contractTypeNum = Number(form.contractType)
    if (contractTypeNum === 2 && !form.contractEndDate) {
      setError('Vyplňte prosím datum ukončení smlouvy (povinné pro smlouvy na dobu určitou).')
      return
    }
    if (form.contractEndDate && form.hireDate && form.contractEndDate < form.hireDate) {
      setError('Datum ukončení nemůže být dříve než datum zahájení.')
      return
    }
    if (form.probationEndDate && form.hireDate && form.probationEndDate < form.hireDate) {
      setError('Datum ukončení nemůže být dříve než datum zahájení.')
      return
    }

    const workScheduleJson = form.scheduleMode === 'custom'
      ? JSON.stringify(schedule)
      : null

    const statusNum = Number(form.status)
    mutation.mutate({
      ...form,
      phone:               form.phone || undefined,
      costCenter:          form.costCenter || undefined,
      personalEmail:       form.personalEmail || undefined,
      personalPhone:       form.personalPhone || undefined,
      nextOfKinName:       form.nextOfKinName || undefined,
      nextOfKinAddress:    form.nextOfKinAddress || undefined,
      nextOfKinEmail:      form.nextOfKinEmail || undefined,
      nextOfKinPhone:      form.nextOfKinPhone || undefined,
      address:             form.address || undefined,
      city:                form.city || undefined,
      postalCode:          form.postalCode || undefined,
      country:             form.country || undefined,
      cooperationStartDate: form.cooperationStartDate || undefined,
      birthDate:           form.birthDate || undefined,
      personalId:          form.personalId || undefined,
      healthInsurance:     form.healthInsurance || undefined,
      healthInsuranceCode: form.healthInsuranceCode || undefined,
      contractType:        contractTypeNum,
      contractEndDate:     contractTypeNum === 2 && form.contractEndDate ? form.contractEndDate : undefined,
      probationEndDate:    form.probationEndDate || undefined,
      departmentName:      form.departmentName || undefined,
      positionName:        form.positionName || undefined,
      employmentType:      Number(form.employmentType),
      weeklyHours:         weeklyHours,   // #23 Fáze 1: týdenní hodiny (primární); backend z nich odvodí %
      // #23: datum platnosti posíláme vždy — nové období (od data) i oprava stávajícího (datum aktuálního období)
      workloadValidFrom:   (form.workloadValidFrom || currentPeriodFrom) || new Date().toISOString().split('T')[0],
      workloadIsNew:       form.workloadIsNew,
      workingRatioPercent: Number(form.workingRatioPercent),
      workScheduleJson:    workScheduleJson ?? undefined,
      status:              statusNum,
      statusFrom:          (statusNum === 3 || statusNum === 4) && form.statusFrom ? form.statusFrom : undefined,
      bankAccount:         form.bankAccount || undefined,
      vehicle:             form.vehicle || undefined,
      child1Name:          form.child1Name || undefined,
      child1PersonalId:    form.child1PersonalId || undefined,
      child1Studies:       form.child1Studies,
      child2Name:          form.child2Name || undefined,
      child2PersonalId:    form.child2PersonalId || undefined,
      child2Studies:       form.child2Studies,
      child3Name:          form.child3Name || undefined,
      child3PersonalId:    form.child3PersonalId || undefined,
      child3Studies:       form.child3Studies,
      child4Name:          form.child4Name || undefined,
      child4PersonalId:    form.child4PersonalId || undefined,
      child4Studies:       form.child4Studies,
      child5Name:          form.child5Name || undefined,
      child5PersonalId:    form.child5PersonalId || undefined,
      child5Studies:       form.child5Studies,
      note:                form.note || undefined,
    })
  }

  if (isEdit && isLoadingDetail) return <Spinner label={t('app.loading')} />

  return (
    <div style={{ maxWidth: 800 }}>
      {/* Page header */}
      <div className={styles.pageHeader}>
        <Button appearance="subtle" icon={<ArrowLeft24Regular />} onClick={() => navigate('/employees')}>
          {t('common.back')}
        </Button>
        <Text size={600} weight="semibold" style={{ flex: 1 }}>
          {isEdit ? 'Upravit zaměstnance' : copyInitialForm ? `Kopie — ${copySource.firstName} ${copySource.lastName}` : 'Přidat zaměstnance'}
        </Text>
      </div>

      <form onSubmit={handleSubmit}>
        {error && (
          <MessageBar intent="error" style={{ marginBottom: '16px' }}>
            <MessageBarBody>{error}</MessageBarBody>
          </MessageBar>
        )}

        {/* ── Základní údaje ── */}
        <div className={styles.card}>
          {sectionChanged('firstName', 'lastName', 'email', 'phone', 'managerId', 'entraObjectId') && (
            <div className={styles.changedStripe} />
          )}
          <div className={styles.cardHeader} onClick={() => toggleSection('basic')}>
            <span className={styles.cardTitle}>Základní údaje</span>
            {openSections.basic ? <ChevronUp20Regular /> : <ChevronDown20Regular />}
          </div>
          {openSections.basic && (
            <>
              {/* Řádek: Jméno | Příjmení */}
              <div className={styles.fieldRow}>
                <div className={mergeClasses(styles.fieldCell, styles.fieldCellBorderRight)}>
                  {cellLabel('Jméno *', 'firstName')}
                  <Input value={form.firstName} onChange={set('firstName')} />
                </div>
                <div className={styles.fieldCell}>
                  {cellLabel('Příjmení *', 'lastName')}
                  <Input value={form.lastName} onChange={set('lastName')} />
                </div>
              </div>

              {/* Řádek: E-mail pracovní | Telefon pracovní */}
              <div className={styles.fieldRow}>
                <div className={mergeClasses(styles.fieldCell, styles.fieldCellBorderRight)}>
                  {cellLabel('E-mail pracovní', ['email', 'entraObjectId'])}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', position: 'relative' }}>
                    <div className={styles.searchRow}>
                      <Input
                        type="email"
                        name="workEmail"
                        autoComplete="work email"
                        value={form.email}
                        style={{ flex: 1 }}
                        onChange={(_, d) => {
                          setForm(f => ({ ...f, email: d.value }))
                          setSearchResults([])
                          setEmailContractorInfo(null)
                        }}
                        onBlur={() => checkContractorEmail(form.email)}
                        onKeyDown={e => e.key === 'Enter' && !IS_MOCK && (e.preventDefault(), handleEntraSearch())}
                      />
                      {!IS_MOCK && (
                        <Button
                          icon={isSearching ? <Spinner size="tiny" /> : <PersonSearch24Regular />}
                          onClick={handleEntraSearch}
                          disabled={isSearching || form.email.length < 2}
                          title="Vyhledat v Entra ID a předvyplnit údaje"
                        />
                      )}
                    </div>
                    {searchResults.length > 0 && (
                      <div className={styles.resultsList}>
                        {searchResults.map(u => (
                          <div key={u.id} className={styles.resultItem} onClick={() => handleSelectEntraUser(u)}>
                            <Text weight="semibold">{u.displayName}</Text>
                            <Text size={200} style={{ display: 'block', color: tokens.colorNeutralForeground3 }}>
                              {u.mail ?? u.userPrincipalName}
                            </Text>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  {!IS_MOCK && (
                    <span className={styles.hint}>Zadejte e-mail a klikněte na ikonu pro načtení z Entra ID</span>
                  )}
                  {emailContractorInfo && (
                    <span style={{ display: 'block', marginTop: 4, fontSize: '11px', color: tokens.colorPaletteYellowForeground1 }}>
                      {emailContractorInfo}
                    </span>
                  )}
                </div>
                <div className={styles.fieldCell}>
                  {cellLabel('Telefon pracovní', 'phone')}
                  <CzPhoneInput value={form.phone ?? ''} onChange={v => setForm(f => ({ ...f, phone: v }))} />
                </div>
              </div>

              {/* Řádek FULL: Manažer */}
              <div className={mergeClasses(styles.fieldRow, styles.fieldRowFull)}>
                <div className={styles.fieldCell}>
                  {cellLabel('Manažer', 'managerId')}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', position: 'relative' }}>
                    {form.managerId ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Text>{form.managerName}</Text>
                        <Button size="small" appearance="subtle" onClick={() => setForm(f => ({ ...f, managerId: undefined, managerName: '' }))}>✕</Button>
                      </div>
                    ) : (
                      <div className={styles.searchRow}>
                        <Input
                          value={managerQuery}
                          onChange={(_, d) => setManagerQuery(d.value)}
                          onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), handleManagerSearch())}
                          style={{ flex: 1 }}
                        />
                        <Button
                          icon={isSearchingManager ? <Spinner size="tiny" /> : <PersonSearch24Regular />}
                          onClick={handleManagerSearch}
                          disabled={isSearchingManager || managerQuery.length < 2}
                        />
                      </div>
                    )}
                    {managerResults.length > 0 && (
                      <div className={styles.resultsList}>
                        {managerResults.map(emp => (
                          <div key={emp.id} className={styles.resultItem} onClick={() => handleSelectManager(emp)}>
                            <Text weight="semibold">{emp.fullName}</Text>
                            {emp.departmentName && (
                              <Text size={200} style={{ display: 'block', color: tokens.colorNeutralForeground3 }}>
                                {emp.departmentName}
                              </Text>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    {managerHint && !form.managerId && (
                      <MessageBar intent="warning" style={{ marginTop: '4px' }}>
                        <MessageBarBody>{managerHint}</MessageBarBody>
                      </MessageBar>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* ── Pracovní zařazení ── */}
        <div className={styles.card}>
          {sectionChanged('departmentName', 'positionName', 'costCenter', 'personalNumber', 'hasMealVouchers') && (
            <div className={styles.changedStripe} />
          )}
          <div className={styles.cardHeader} onClick={() => toggleSection('assignment')}>
            <span className={styles.cardTitle}>Pracovní zařazení</span>
            {openSections.assignment ? <ChevronUp20Regular /> : <ChevronDown20Regular />}
          </div>
          {openSections.assignment && (
            <>
              {/* Řádek: Oddělení | Pracovní pozice */}
              <div className={styles.fieldRow}>
                <div className={mergeClasses(styles.fieldCell, styles.fieldCellBorderRight)}>
                  {cellLabel('Oddělení', 'departmentName')}
                  <Combobox
                    freeform
                    placeholder="— vyberte nebo napište —"
                    value={form.departmentName}
                    onChange={e => setForm(f => ({ ...f, departmentName: e.target.value }))}
                    onOptionSelect={(_, d) => setForm(f => ({ ...f, departmentName: d.optionText ?? '' }))}
                  >
                    {deptNames.map(name => <Option key={name} value={name}>{name}</Option>)}
                  </Combobox>
                  <span className={styles.hint}>Vyberte existující nebo napište nové</span>
                </div>
                <div className={styles.fieldCell}>
                  {cellLabel('Pracovní pozice', 'positionName')}
                  <Combobox
                    freeform
                    placeholder="— vyberte nebo napište —"
                    value={form.positionName}
                    onChange={e => setForm(f => ({ ...f, positionName: e.target.value }))}
                    onOptionSelect={(_, d) => setForm(f => ({ ...f, positionName: d.optionText ?? '' }))}
                  >
                    {positionNames.map(name => <Option key={name} value={name}>{name}</Option>)}
                  </Combobox>
                  <span className={styles.hint}>Vyberte existující nebo napište nové</span>
                </div>
              </div>

              {/* Řádek: Středisko | Os.č. */}
              <div className={styles.fieldRow}>
                <div className={mergeClasses(styles.fieldCell, styles.fieldCellBorderRight)}>
                  {cellLabel('Středisko', 'costCenter')}
                  <Input value={form.costCenter ?? ''} onChange={set('costCenter')} />
                </div>
                <div className={styles.fieldCell}>
                  {cellLabel('Osobní číslo', 'personalNumber')}
                  <Input value={form.personalNumber ?? ''} onChange={set('personalNumber')} />
                </div>
              </div>

              {/* Řádek: Stravenky */}
              <div className={styles.fieldRow}>
                <div className={mergeClasses(styles.fieldCell, styles.fieldCellBorderRight)}>
                  {cellLabel('Stravenky', 'hasMealVouchers')}
                  <Checkbox
                    label="Má nárok na stravenky"
                    checked={form.hasMealVouchers ?? false}
                    disabled={!mealVouchersEligible}
                    onChange={(_, d) => setForm(f => ({ ...f, hasMealVouchers: !!d.checked }))}
                  />
                  {!mealVouchersEligible && (
                    <span style={{ fontSize: '12px', color: '#888', marginTop: '2px', display: 'block' }}>
                      Dostupné pouze pro HPP zaměstnance ve stavu Aktivní
                    </span>
                  )}
                </div>
                <div className={styles.fieldCell} />
              </div>
            </>
          )}
        </div>

        {/* ── Smlouva ── */}
        <div className={styles.card}>
          {sectionChanged('hireDate', 'cooperationStartDate', 'employmentType', 'probationEndDate', 'status', 'statusFrom', 'contractType', 'contractEndDate') && (
            <div className={styles.changedStripe} />
          )}
          <div className={styles.cardHeader} onClick={() => toggleSection('contract')}>
            <span className={styles.cardTitle}>Smlouva</span>
            {openSections.contract ? <ChevronUp20Regular /> : <ChevronDown20Regular />}
          </div>
          {openSections.contract && (
            <>
              {/* Řádek: Datum nástupu | Zahájení spolupráce */}
              <div className={styles.fieldRow}>
                <div className={mergeClasses(styles.fieldCell, styles.fieldCellBorderRight)}>
                  {cellLabel('Datum nástupu *', 'hireDate')}
                  <NativeDateInput value={form.hireDate} onChange={v => setForm(f => ({ ...f, hireDate: v }))} />
                </div>
                <div className={styles.fieldCell}>
                  {cellLabel('Zahájení spolupráce (pro výročí)', 'cooperationStartDate')}
                  <NativeDateInput value={form.cooperationStartDate ?? ''} onChange={v => setForm(f => ({ ...f, cooperationStartDate: v }))} />
                </div>
              </div>

              {/* Řádek: Typ pracovního poměru | Ukončení zkušební doby */}
              <div className={styles.fieldRow}>
                <div className={mergeClasses(styles.fieldCell, styles.fieldCellBorderRight)}>
                  {cellLabel('Typ pracovního poměru *', 'employmentType')}
                  <Select value={String(form.employmentType)} onChange={set('employmentType')}>
                    {EMPLOYMENT_TYPES.map(et => (
                      <option key={et.value} value={et.value}>{et.label}</option>
                    ))}
                  </Select>
                </div>
                <div className={styles.fieldCell}>
                  {cellLabel('Ukončení zkušební doby', 'probationEndDate')}
                  <NativeDateInput value={form.probationEndDate} onChange={v => setForm(f => ({ ...f, probationEndDate: v }))} min={form.hireDate || undefined} />
                </div>
              </div>

              {/* Řádek: Stav zaměstnance | Datum ukončení PP nebo Začátek rodičovské */}
              <div className={styles.fieldRow}>
                <div className={mergeClasses(styles.fieldCell, styles.fieldCellBorderRight)}>
                  {cellLabel('Stav zaměstnance', 'status')}
                  <Select value={String(form.status)} onChange={set('status')}>
                    <option value="1">Aktivní</option>
                    <option value="2">Nenastoupil</option>
                    <option value="4">Rodičovská dovolená</option>
                    <option value="3">Ukončen</option>
                  </Select>
                </div>
                <div className={styles.fieldCell}>
                  {(Number(form.status) === 3 || Number(form.status) === 4) ? (
                    <>
                      {cellLabel(
                        Number(form.status) === 3 ? 'Datum ukončení PP *' : 'Začátek rodičovské *',
                        'statusFrom'
                      )}
                      <NativeDateInput value={form.statusFrom} onChange={v => setForm(f => ({ ...f, statusFrom: v }))} />
                    </>
                  ) : null}
                </div>
              </div>

              {/* Řádek: Typ smlouvy | Datum ukončení smlouvy */}
              <div className={styles.fieldRow}>
                <div className={mergeClasses(styles.fieldCell, styles.fieldCellBorderRight)}>
                  {cellLabel('Typ smlouvy', 'contractType')}
                  <Select
                    value={String(form.contractType)}
                    onChange={(_, d) => setForm(f => ({ ...f, contractType: Number(d.value) }))}
                  >
                    {CONTRACT_TYPES.map(ct => (
                      <option key={ct.value} value={ct.value}>{ct.label}</option>
                    ))}
                  </Select>
                </div>
                <div className={styles.fieldCell}>
                  {Number(form.contractType) === 2 ? (
                    <>
                      {cellLabel('Datum ukončení smlouvy *', 'contractEndDate')}
                      <NativeDateInput value={form.contractEndDate} onChange={v => setForm(f => ({ ...f, contractEndDate: v }))} min={form.hireDate || undefined} />
                    </>
                  ) : null}
                </div>
              </div>
            </>
          )}
        </div>

        {/* ── Pracovní rozvrh ── */}
        <div className={styles.card}>
          {sectionChanged('scheduleMode', 'workingRatioPercent', 'weeklyHours') && (
            <div className={styles.changedStripe} />
          )}
          <div className={styles.cardHeader} onClick={() => toggleSection('schedule')}>
            <span className={styles.cardTitle}>Pracovní rozvrh</span>
            {openSections.schedule ? <ChevronUp20Regular /> : <ChevronDown20Regular />}
          </div>
          {openSections.schedule && (
            <>
              {/* Řádek: Typ rozvrhu | Úvazek % (jen standard) */}
              <div className={styles.fieldRow}>
                <div className={mergeClasses(styles.fieldCell, styles.fieldCellBorderRight)}>
                  {cellLabel('Typ rozvrhu', 'scheduleMode')}
                  <Select
                    value={form.scheduleMode}
                    onChange={(_, d) => setForm(f => ({ ...f, scheduleMode: d.value as 'standard' | 'custom' }))}
                  >
                    <option value="standard">Standardní (hodin/týden)</option>
                    <option value="custom">Vlastní (hodiny po dnech)</option>
                  </Select>
                </div>
                <div className={styles.fieldCell}>
                  {form.scheduleMode === 'standard' && (
                    <>
                      {cellLabel('Hodin/týden', 'weeklyHours')}
                      <Input
                        type="number"
                        step="0.5"
                        min="1"
                        max="40"
                        value={String(form.weeklyHours)}
                        onChange={set('weeklyHours')}
                        style={{ maxWidth: 120 }}
                      />
                    </>
                  )}
                </div>
              </div>

              {/* Vlastní rozvrh — day grid */}
              {form.scheduleMode === 'custom' && (
                <div className={mergeClasses(styles.fieldRow, styles.fieldRowFull)}>
                  <div className={styles.fieldCell}>
                    {cellLabel('Rozvrh po dnech')}
                    <div className={styles.dayGrid}>
                      {DAY_KEYS.map(d => (
                        <div key={d} style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          <span className={styles.fieldLabel}>{DAY_LABELS[d]}</span>
                          <Input
                            type="text"
                            inputMode="decimal"
                            value={dayInputs[d] !== undefined ? dayInputs[d]! : String(schedule[d]).replace('.', ',')}
                            onChange={setDay(d)}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* #23 Fáze 2: nové období od data (default) vs. oprava stávajícího úvazku v místě (jen při editaci) */}
              {isEdit && (
                <div className={mergeClasses(styles.fieldRow, styles.fieldRowFull)}>
                  <div className={styles.fieldCell}>
                    {cellLabel('Změna úvazku', 'workloadIsNew')}
                    <RadioGroup
                      value={form.workloadIsNew ? 'new' : 'fix'}
                      onChange={(_, d) => {
                        const isNew = d.value === 'new'
                        const today = new Date().toISOString().split('T')[0]
                        setForm(f => ({
                          ...f,
                          workloadIsNew: isNew,
                          // při přepnutí: nové → dnes; oprava → datum platnosti stávajícího období
                          workloadValidFrom: isNew ? today : (currentPeriodFrom || today),
                        }))
                      }}
                    >
                      <Radio value="new" label="Nový úvazek od data — uloží se do historie (stávající zůstane zachován)" />
                      <Radio value="fix" label="Jen oprava stávajícího úvazku — bez nového období" />
                    </RadioGroup>
                    {form.workloadIsNew ? (
                      <div style={{ marginTop: 6 }}>
                        {cellLabel('Nový úvazek platí od', 'workloadValidFrom')}
                        <NativeDateInput
                          value={form.workloadValidFrom}
                          onChange={v => setForm(f => ({ ...f, workloadValidFrom: v }))}
                        />
                        <span className={styles.hint}>Od tohoto data platí nový úvazek. Změňte hodiny/rozvrh výše a uložte — vznikne nové období v historii.</span>
                      </div>
                    ) : canEditCurrentFrom ? (
                      <div style={{ marginTop: 6 }}>
                        {cellLabel('Úvazek platí od', 'workloadValidFrom')}
                        <NativeDateInput
                          value={form.workloadValidFrom || currentPeriodFrom}
                          onChange={v => setForm(f => ({ ...f, workloadValidFrom: v }))}
                        />
                        <span className={styles.hint}>Oprava stávajícího (aktuálního) období — upravíte hodiny/rozvrh výše i datum jeho platnosti, bez založení nového období. Starší období upravíte níže v „Historie úvazku".</span>
                      </div>
                    ) : (
                      <span className={styles.hint}>Změna hodin/rozvrhu výše přepíše stávající úvazek (pro opravu překlepu). Počátek jediného období odpovídá datu nástupu — upravíte jej v poli „Datum nástupu".</span>
                    )}
                  </div>
                </div>
              )}

              {/* #23 Fáze 2: editace historie úvazku (jen při editaci, jen pokud existuje víc období) */}
              {isEdit && <WorkloadHistoryEditor employeeId={Number(id)} />}

              {/* Hint s fondem hodin */}
              <div className={mergeClasses(styles.fieldRow, styles.fieldRowFull)}>
                <div className={styles.fieldCell}>
                  <span className={styles.hint}>
                    Celkový fond hodin: <strong>{weeklyHours.toFixed(weeklyHours % 1 === 0 ? 0 : 1).replace('.', ',')} h / týden</strong>
                    {' · '}
                    Nárok na dovolenou bude vypočten automaticky dle data nástupu.
                  </span>
                </div>
              </div>
            </>
          )}
        </div>

        {/* ── Mzdové a pojistné údaje ── */}
        <div className={styles.card}>
          {sectionChanged('healthInsurance', 'healthInsuranceCode', 'bankAccount', 'vehicle') && (
            <div className={styles.changedStripe} />
          )}
          <div className={styles.cardHeader} onClick={() => toggleSection('payroll')}>
            <span className={styles.cardTitle}>Mzdové a pojistné údaje</span>
            {openSections.payroll ? <ChevronUp20Regular /> : <ChevronDown20Regular />}
          </div>
          {openSections.payroll && (
            <>
              {/* Řádek: Zdravotní pojišťovna | Kód pojišťovny */}
              <div className={styles.fieldRow}>
                <div className={mergeClasses(styles.fieldCell, styles.fieldCellBorderRight)}>
                  {cellLabel('Zdravotní pojišťovna', 'healthInsurance')}
                  <Combobox
                    freeform
                    value={form.healthInsurance ?? ''}
                    onInput={e => setForm(f => ({ ...f, healthInsurance: (e.target as HTMLInputElement).value }))}
                    onOptionSelect={(_, data) => {
                      const opt = healthInsuranceOptions.find(o => o.name === data.optionValue)
                      setForm(f => ({
                        ...f,
                        healthInsurance: data.optionValue ?? '',
                        healthInsuranceCode: opt?.code ?? f.healthInsuranceCode,
                      }))
                    }}
                  >
                    {healthInsuranceOptions.map(o => (
                      <Option key={o.code} value={o.name} text={o.name}>{o.name} ({o.code})</Option>
                    ))}
                  </Combobox>
                </div>
                <div className={styles.fieldCell}>
                  {cellLabel('Kód pojišťovny', 'healthInsuranceCode')}
                  <Input
                    value={form.healthInsuranceCode ?? ''}
                    onChange={e => {
                      const code = e.target.value.slice(0, 3)
                      const matched = healthInsuranceOptions.find(o => o.code === code)
                      setForm(f => ({
                        ...f,
                        healthInsuranceCode: code,
                        healthInsurance: matched ? matched.name : f.healthInsurance,
                      }))
                    }}
                    maxLength={3}
                    style={{ maxWidth: 100 }}
                  />
                </div>
              </div>

              {/* Řádek FULL: Bankovní spojení */}
              <div className={mergeClasses(styles.fieldRow, styles.fieldRowFull)}>
                <div className={styles.fieldCell}>
                  {cellLabel('Bankovní spojení', 'bankAccount')}
                  <Input value={form.bankAccount ?? ''} onChange={set('bankAccount')} maxLength={50} />
                  <span className={styles.hint}>např. 123456-1234567890/0800</span>
                </div>
              </div>

              {/* Řádek: Vozidlo */}
              <div className={mergeClasses(styles.fieldRow, styles.fieldRowFull)}>
                <div className={styles.fieldCell}>
                  {cellLabel('Vozidlo', 'vehicle')}
                  <Input value={form.vehicle ?? ''} onChange={set('vehicle')} maxLength={100} />
                </div>
              </div>
            </>
          )}
        </div>

        {/* ── Soukromé údaje ── */}
        <div className={styles.card}>
          {sectionChanged('birthDate', 'personalId', 'personalEmail', 'personalPhone', 'nextOfKinName', 'nextOfKinAddress', 'nextOfKinEmail', 'nextOfKinPhone', 'address', 'city', 'postalCode', 'country') && (
            <div className={styles.changedStripe} />
          )}
          <div className={styles.cardHeader} onClick={() => toggleSection('private')}>
            <span className={styles.cardTitle}>Soukromé údaje</span>
            {openSections.private ? <ChevronUp20Regular /> : <ChevronDown20Regular />}
          </div>
          {openSections.private && (
            <>
              {/* Řádek: Datum narození | Rodné číslo */}
              <div className={styles.fieldRow}>
                <div className={mergeClasses(styles.fieldCell, styles.fieldCellBorderRight)}>
                  {cellLabel('Datum narození', 'birthDate')}
                  <NativeDateInput value={form.birthDate ?? ''} onChange={v => setForm(f => ({ ...f, birthDate: v }))} />
                </div>
                <div className={styles.fieldCell}>
                  {cellLabel('Rodné číslo', 'personalId')}
                  <Field
                    validationState={personalIdError ? 'error' : undefined}
                    validationMessage={personalIdError ?? undefined}
                  >
                    <Input
                      value={form.personalId ?? ''}
                      onChange={(_, d) => {
                        setForm(f => ({ ...f, personalId: d.value }))
                        setPersonalIdError(validateCzPersonalId(d.value))
                      }}
                      onBlur={() => {
                        if (form.personalId) {
                          setForm(f => ({ ...f, personalId: formatCzPersonalId(f.personalId ?? '') }))
                        }
                      }}
                      maxLength={11}
                    />
                  </Field>
                </div>
              </div>

              {/* Řádek: Osobní e-mail | Osobní telefon */}
              <div className={styles.fieldRow}>
                <div className={mergeClasses(styles.fieldCell, styles.fieldCellBorderRight)}>
                  {cellLabel('Osobní e-mail', 'personalEmail')}
                  <Input
                    type="email"
                    name="personalEmail"
                    autoComplete="off"
                    value={form.personalEmail ?? ''}
                    onChange={(_, d) => setForm(f => ({ ...f, personalEmail: d.value }))}
                  />
                </div>
                <div className={styles.fieldCell}>
                  {cellLabel('Osobní telefon', 'personalPhone')}
                  <CzPhoneInput value={form.personalPhone ?? ''} onChange={v => setForm(f => ({ ...f, personalPhone: v }))} />
                </div>
              </div>

              {/* Řádek: Adresa | Obec | PSČ | Stát */}
              <div className={mergeClasses(styles.fieldRow, styles.fieldRowFull)}>
                <div className={styles.fieldCell}>
                  <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 80px 60px', gap: '16px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      {cellLabel('Adresa: Ulice včetně čísla', 'address')}
                      <Input value={form.address ?? ''} onChange={set('address')} />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      {cellLabel('Obec', 'city')}
                      <Input value={form.city ?? ''} onChange={set('city')} />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      {cellLabel('PSČ', 'postalCode')}
                      <Input value={form.postalCode ?? ''} onChange={set('postalCode')} />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      {cellLabel('Stát', 'country')}
                      <Input value={form.country ?? 'CZ'} onChange={set('country')} maxLength={2}
                        style={{ textTransform: 'uppercase' }} />
                    </div>
                  </div>
                </div>
              </div>

              {/* Řádek FULL: Osoba blízká — řádek 1 (Jméno | Adresa) */}
              <div className={mergeClasses(styles.fieldRow, styles.fieldRowFull)}>
                <div className={styles.fieldCell}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      {cellLabel('Osoba blízká: Jméno', 'nextOfKinName')}
                      <Input value={form.nextOfKinName ?? ''} onChange={set('nextOfKinName')} />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      {cellLabel('Adresa', 'nextOfKinAddress')}
                      <Input value={form.nextOfKinAddress ?? ''} onChange={set('nextOfKinAddress')} />
                    </div>
                  </div>
                </div>
              </div>

              {/* Řádek FULL: Osoba blízká — řádek 2 (Telefon | E-mail) */}
              <div className={mergeClasses(styles.fieldRow, styles.fieldRowFull)}>
                <div className={styles.fieldCell}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      {cellLabel('Osoba blízká: Telefon', 'nextOfKinPhone')}
                      <Input value={form.nextOfKinPhone ?? ''} onChange={set('nextOfKinPhone')} />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      {cellLabel('E-mail', 'nextOfKinEmail')}
                      <Input value={form.nextOfKinEmail ?? ''} onChange={set('nextOfKinEmail')} />
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* ── Děti ── */}
        <div className={styles.card}>
          {sectionChanged('child1Name', 'child1PersonalId', 'child1Studies', 'child2Name', 'child2PersonalId', 'child2Studies', 'child3Name', 'child3PersonalId', 'child3Studies', 'child4Name', 'child4PersonalId', 'child4Studies', 'child5Name', 'child5PersonalId', 'child5Studies') && (
            <div className={styles.changedStripe} />
          )}
          <div className={styles.cardHeader} onClick={() => toggleSection('children')}>
            <span className={styles.cardTitle}>Děti</span>
            {openSections.children ? <ChevronUp20Regular /> : <ChevronDown20Regular />}
          </div>
          {openSections.children && (
            <>
              {childRows.map((row, i) => (
                <div key={i} className={mergeClasses(styles.fieldRow, styles.fieldRowFull)}>
                  <div className={styles.fieldCell}>
                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr auto auto', gap: '16px', alignItems: 'end' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        <span className={styles.fieldLabel}>JMÉNO</span>
                        <Input
                          value={row.name}
                          onChange={(_, d) => { const r = [...childRows]; r[i] = { ...r[i], name: d.value }; updateChildRows(r) }}
                        />
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        <span className={styles.fieldLabel}>RODNÉ ČÍSLO</span>
                        <Input
                          value={row.personalId}
                          maxLength={30}
                          onChange={(_, d) => { const r = [...childRows]; r[i] = { ...r[i], personalId: d.value }; updateChildRows(r) }}
                        />
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        <span className={styles.fieldLabel}>STUDUJE</span>
                        <Checkbox
                          checked={row.studies}
                          onChange={(_, d) => { const r = [...childRows]; r[i] = { ...r[i], studies: !!d.checked }; updateChildRows(r) }}
                        />
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        <span className={styles.fieldLabel}>&nbsp;</span>
                        <Button
                          appearance="subtle"
                          icon={<Delete24Regular />}
                          title="Odebrat dítě"
                          onClick={() => updateChildRows(childRows.filter((_, j) => j !== i))}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              {childRows.length < 5 && (
                <div style={{ padding: '4px 24px 16px' }}>
                  <Button
                    appearance="subtle"
                    icon={<Add24Regular />}
                    onClick={() => updateChildRows([...childRows, { name: '', personalId: '', studies: true }])}
                  >
                    Přidat dítě
                  </Button>
                </div>
              )}
            </>
          )}
        </div>

        {/* ── Poznámka ── */}
        {(isHR || isAdmin) && (
          <div className={styles.card}>
            {sectionChanged('note') && (
              <div className={styles.changedStripe} />
            )}
            <div className={styles.cardHeader} onClick={() => toggleSection('note')}>
              <span className={styles.cardTitle}>Poznámka</span>
              {openSections.note ? <ChevronUp20Regular /> : <ChevronDown20Regular />}
            </div>
            {openSections.note && (
              <div className={mergeClasses(styles.fieldRow, styles.fieldRowFull)}>
                <div className={styles.fieldCell}>
                  {cellLabel('Interní poznámka', 'note')}
                  <Textarea
                    value={form.note ?? ''}
                    onChange={(_, d) => setForm(f => ({ ...f, note: d.value }))}
                    rows={4}
                    resize="vertical"
                    style={{ width: '100%' }}
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {/* Floating bar */}
        <div className={styles.floatingBar}>
          {/* Trvale odstranit — jen když je Terminated */}
          {isEdit && canDelete && detail?.status === 3 && (
            <Button
              appearance="subtle"
              icon={<Delete24Regular />}
              type="button"
              style={{ color: '#f87171' }}
              onMouseEnter={e => (e.currentTarget.style.backgroundColor = 'rgba(239,68,68,0.15)')}
              onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
              onClick={() => setDeleteOpen(true)}
            >
              Trvale odstranit
            </Button>
          )}
          <span className={styles.floatingHint}>
            {isDirty ? '✏ Neuložené změny' : ''}
          </span>
          {isEdit && canDelete && (
            <Button
              appearance="subtle"
              icon={<DocumentCopy24Regular />}
              type="button"
              style={{ color: 'rgba(255,255,255,0.8)' }}
              onMouseEnter={e => (e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.12)')}
              onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
              onClick={() => navigate('/employees/new', { state: { copyFrom: detail } })}
            >
              Kopírovat záznam
            </Button>
          )}
          <Button
            appearance="subtle"
            type="button"
            style={{ color: 'rgba(255,255,255,0.8)' }}
            onMouseEnter={e => (e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.12)')}
            onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
            onClick={() => navigate('/employees')}
          >
            Zrušit editaci
          </Button>
          <Button
            appearance="primary"
            icon={mutation.isPending ? <Spinner size="tiny" /> : <Save24Regular />}
            type="submit"
            disabled={mutation.isPending}
          >
            {t('common.save')}
          </Button>
        </div>
      </form>

      {/* Dialog: trvalé smazání */}
      <Dialog open={deleteOpen} onOpenChange={(_, s) => setDeleteOpen(s.open)}>
        <DialogSurface>
          <DialogTitle>Trvale odstranit zaměstnance</DialogTitle>
          <DialogBody>
            <DialogContent>
              Opravdu chcete trvale odstranit zaměstnance <strong>{form.firstName} {form.lastName}</strong>?
              Tato akce je nevratná a smaže všechna přidružená data (dovolené, dokumenty).
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setDeleteOpen(false)}>Zrušit</Button>
              <Button
                appearance="primary"
                style={{ backgroundColor: tokens.colorPaletteRedBackground3, color: '#fff' }}
                icon={permanentDeleteMutation.isPending ? <Spinner size="tiny" /> : <Delete24Regular />}
                disabled={permanentDeleteMutation.isPending}
                onClick={() => permanentDeleteMutation.mutate()}
              >
                Trvale odstranit
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Dialog: neuložené změny */}
      <UnsavedChangesDialog blocker={blocker} />
    </div>
  )
}
